<template>
  <div :class="'form-group col-md-' + width + (offset ? ' col-md-offset-right-' + offset : '')">
    <div class="form-el">
      <label>{{ stimulus }}</label>
      <span v-if="required" class="required-red">*</span>
      <a v-if="tooltip" class="mouse-pointer mx-1 form-tooltip">
        <icon :icon="'question-circle'" class="form-tooltip-icon" :size="'sm'"></icon>
        <span class="form-tooltiptext">{{ tooltip }}</span>
      </a>
    </div>

    <div
      v-if="
        !viewMode &&
        ((['singleFileUpload', 'certificateUpload'].includes(subtype) && uploadedFiles.length === 0) ||
          subtype === 'multipleFileUpload')
      "
      class="custom-file form-el"
    >
      <input
        :id="'fileupload' + id"
        ref="fileInput"
        type="file"
        :multiple="isMultiple"
        :warningmessage="warningMessage"
        :required="isRequiredAttr"
        :disabled="isDisabledAttr"
        class="custom-file-input form-control"
        :formelementid="id"
        @input="checkValid($event.target)"
      />
      <label
        :style="labelStyle"
        data-browse="Browse"
        :class="'custom-file-label text-truncate' + (isBusy ? ' input-loading-file' : '')"
        :for="'fileupload' + id"
      >
        {{ filename }}
      </label>
    </div>

    <div v-if="viewMode && uploadedFiles.length === 0" class="form-el">
      <input type="text" placeholder="No Files Uploaded" disabled :formelementid="id" class="form-control" />
    </div>

    <div>
      <div
        v-for="(file, index) in uploadedFiles"
        :key="file.fileId"
        :class="(index === 0 && viewMode ? 'file-first ' : '') + 'download-link-container'"
        :style="fileContainerStyle"
      >
        <a
          v-if="subtype === 'certificateUpload'"
          class="download-link mr-2"
          :aria-text="'Download ' + file.fileName"
          :title="'Download ' + file.fileName"
          @click="fileDownload(file.fileId)"
        >
          {{ file.fileName }}
        </a>
        <a
          v-else
          class="download-link mr-2"
          :aria-text="'Download ' + file.fileName"
          :title="'Download ' + file.fileName"
          @click="fileDownload(file.fileId)"
        >
          {{ file.fileName }}
        </a>
        <a
          v-if="!viewMode"
          class="download-link-delete"
          :aria-text="'Delete ' + file.fileName"
          :title="'Delete ' + file.fileName"
          @click="fileDelete(file.fileId, file.fileName)"
        >
          <icon class="link-delete" :icon="'times'"></icon>
        </a>
      </div>
      <div>
        <modal
          v-show="isFileModalVisible"
          :is-modal-visible="isFileModalVisible"
          :must-view-all="false"
          :modal-busy="isBusy"
          @close="closeFileModal"
        >
          <h2 slot="header">File View</h2>
          <div slot="body" class="text-left row">
            <div v-if="isFileModalVisible" class="col">
              <docViewer
                :download-button-text="'Download File'"
                :file-code="'Certificate'"
                :file-url="filePath"
                :show-download-button="false"
                :show-cert-download-button="true"
              ></docViewer>
            </div>
          </div>
        </modal>
      </div>
    </div>
  </div>
</template>

<script>
import docViewer from '../utility/docViewer';

export default {
  name: 'FileUpload',
  components: {
    docViewer
  },
  props: {
    id: {
      type: Number,
      required: true
    },
    stimulus: {
      type: String,
      required: true
    },
    required: {
      type: Boolean,
      default: false
    },
    width: {
      type: Number,
      default: 12
    },
    warningMessage: {
      type: String,
      default: 'Please select a file.'
    },
    value: {
      type: String,
      default: ''
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    choices: {
      type: Array,
      required: true
    },
    offset: {
      type: Number,
      default: 0
    },
    tooltip: {
      type: String,
      default: ''
    },
    arguments: {
      type: Object,
      default: () => ({})
    },
    files: {
      type: Array,
      default: () => []
    },
    formId: {
      type: Number,
      required: true
    },
    viewMode: {
      type: Boolean,
      required: true
    },
    entityTypeId: {
      type: Number,
      required: true
    },
    subtype: {
      type: String,
      default: 'multipleFileUpload'
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    selectedReference: {
      type: Object,
      default: () => ({})
    },
    primaryEntity: {
      type: Object,
      default: () => ({})
    },
    startingValue: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      filename: '',
      uploadedFiles: [],
      fileSetId: '',
      errors: '',
      isBusy: false,
      supportedTypes: ['image/', 'text/', 'application/vnd.openxmlformats-officedocument', 'application/pdf'],
      filePath: '',
      isFileModalVisible: false
    };
  },
  computed: {
    isMultiple() {
      return this.subtype === 'multipleFileUpload';
    },
    isRequiredAttr() {
      return this.required && this.uploadedFiles.length === 0;
    },
    isDisabledAttr() {
      return this.readOnly;
    },
    labelStyle() {
      const color = this.filename !== this.stimulus && !this.readOnly ? 'black' : 'grey';
      return { color };
    },
    fileContainerStyle() {
      return this.readOnly ? { backgroundColor: '#e9ecef' } : {};
    }
  },
  created() {
    this.uploadedFiles = this.files;
    if (this.uploadedFiles && this.uploadedFiles[0]) {
      this.fileSetId = this.uploadedFiles[0].fileSetId;
    }
    this.filename = this.stimulus;
  },
  methods: {
    async checkValid(control) {
      control.setCustomValidity('');
      if (control.files && control.files.length > 0) {
        const totalFiles = this.uploadedFiles.length + control.files.length;
        if (totalFiles > 24) {
          this.$dialogs.alert(
            `You can upload a maximum of 24 files. You already have ${this.uploadedFiles.length} file(s) uploaded.`,
            {
              title: 'Maximum Files Exceeded',
              size: 'md'
            }
          );
          this.reset();
          return;
        }

        if (
          ['singleFileUpload', 'certificateUpload'].includes(this.subtype) &&
          this.uploadedFiles &&
          this.uploadedFiles.length > 0
        ) {
          const options = { title: 'Error', size: 'md' };
          const errMsg = 'Only one file can be uploaded to this record';
          this.$dialogs.alert(errMsg, options);
          return;
        }

        const filesToUpload = [];
        for (let i = 0; i < control.files.length; i++) {
          const file = control.files[i];
          if (file && file.name && file.type) {
            if (this.isNewFile(file.name)) {
              if (this.isSupportedFileFormat(file.type)) {
                filesToUpload.push(file);
              } else {
                const options = { title: 'Error', size: 'md' };
                const errMsg = 'The file type "' + file.type + '" is not supported';
                this.$dialogs.alert(errMsg, options);
              }
            } else {
              const options = { title: 'Error', size: 'md' };
              const errMsg = 'A file with the name "' + file.name + '" has already been uploaded';
              this.$dialogs.alert(errMsg, options);
            }
          }
        }

        if (filesToUpload.length > 0) {
          this.isBusy = true;
          this.$emit('busy', true);
          let successCount = 0;

          for (const file of filesToUpload) {
            this.filename = file.name;
            try {
              await this.fileUpload(file);
              successCount++;
            } catch (error) {
              break;
            }
          }

          this.reset();
          this.isBusy = false;
          this.$emit('busy', false);

          if (successCount > 0) {
            const options = { title: 'Success' };
            const msg =
              successCount === 1
                ? 'Your file has been uploaded successfully.'
                : `Successfully uploaded ${successCount} file(s).`;
            this.$dialogs.alert(msg, options);
          }
        }
      } else if (this.required && this.uploadedFiles.length === 0) {
        control.setCustomValidity(this.warningMessage);
        control.reportValidity();
      }
    },
    isNewFile(fileName) {
      let isNew = true;
      if (this.uploadedFiles && this.uploadedFiles.length > 0) {
        for (let i = 0; i < this.uploadedFiles.length; i++) {
          const existingFile = this.uploadedFiles[i];
          if (existingFile.fileName && existingFile.fileName === fileName) {
            isNew = false;
            break;
          }
        }
      }
      return isNew;
    },
    isSupportedFileFormat(fileType) {
      let isSupported = false;
      if (this.supportedTypes && this.supportedTypes.length > 0) {
        for (let i = 0; i < this.supportedTypes.length; i++) {
          const supportedType = this.supportedTypes[i];
          if (supportedType && fileType.substring(0, supportedType.length) === supportedType) {
            isSupported = true;
            break;
          }
        }
      }
      return isSupported;
    },
    fileUpload(file) {
      const payload = {
        file: file,
        fileSetId: this.fileSetId,
        entityTypeId: this.entityTypeId,
        formelementid: this.$vnode.key
      };

      return this.$store
        .dispatch('fileUploadRequest', payload)
        .then((result) => {
          this.uploadedFiles.push(result[0]);
          if (!this.fileSetId) {
            this.fileSetId = result[0].fileSetId;
          }
          this.$emit('input', this.fileSetId);
          this.$emit('dirty', true);
        })
        .catch((errors) => {
          const options = { title: 'Error' };
          this.$dialogs.alert(
            `The file "${file.name}" could not be uploaded.<br>Please refresh the page and try again.`,
            options
          );
          this.errors = errors;
          throw errors;
        });
    },
    fileDownload(fileKey) {
      this.isBusy = true;
      this.$store
        .dispatch('fileDownloadRequest', fileKey)
        .then((result) => {
          this.isBusy = false;
          if (result.headers['content-type'] === 'application/pdf') {
            this.isFileModalVisible = true;
          }
          if (window.navigator.msSaveOrOpenBlob) {
            const url = window.navigator.msSaveOrOpenBlob(new Blob([result.data]), 'file');
            if (result.headers['content-type'] === 'application/pdf') {
              this.filePath = url;
            } else {
              window.open(url);
            }
          } else {
            const url = window.URL.createObjectURL(new Blob([result.data], { type: result.headers['content-type'] }));
            if (result.headers['content-type'] === 'application/pdf') {
              this.filePath = url;
            } else {
              const link = document.createElement('a');
              link.href = url;
              const filename = result.headers['content-disposition'].split('filename=')[1].split('; filename*=')[0];
              link.setAttribute('download', filename.replace(/"/g, ''));
              document.body.appendChild(link);
              link.click();
            }
          }
        })
        .catch((errors) => {
          this.isBusy = false;
          this.errors = errors;
        });
    },
    fileDelete(fileKey, fileName) {
      const options = { title: 'Confirm Delete', cancelLabel: 'Cancel' };
      this.$dialogs
        .confirm('Are you sure you want to delete "' + fileName + '"? This cannot be undone.', options)
        .then((res) => {
          if (res && res.ok && res.ok === true) {
            this.isBusy = true;
            const primaryEntityId = this.primaryEntity && this.primaryEntity.Id ? this.primaryEntity.Id : '';
            const body = { fileKey: fileKey, entityTypeId: this.entityTypeId, primaryEntityId: primaryEntityId };
            this.$store
              .dispatch('fileDeleteRequest', body)
              .then(() => {
                this.uploadedFiles = this.uploadedFiles.filter((item) => item.fileId !== fileKey);
                if (this.uploadedFiles.length === 0) {
                  this.$emit('input', '');
                  this.fileSetId = '';
                }
                this.isBusy = false;
                this.$emit('dirty', true);
              })
              .catch((errors) => {
                this.isBusy = false;
                this.errors = errors;
              });
          }
        })
        .catch((error) => {
          this.errors = error;
        });
    },
    reset() {
      const input = this.$refs.fileInput;
      if (input) {
        input.type = 'text';
        input.type = 'file';
      }
      this.filename = this.stimulus;
    },
    closeFileModal() {
      this.isFileModalVisible = false;
    }
  }
};
</script>
